import { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Tabs,
  Tab,
  TextField,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import TradingViewWidget from "../components/TradingViewChart";
import { usePortfolio } from "../hooks/usePortfolio";
import { useTrade } from "../hooks/useTrade";
import {
  calculatePercentage,
  calculatePnlPercentage,
  classNames,
  formatPrice,
} from "../utils/utils";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { AuthContext } from "../contexts/AuthContext";
import { usePortfolioStore } from "../store/usePortfolioStore";
import api from "../services/api";
import moment from "moment";

// Modern Options-aligned chart colors & design tokens
const theme = {
  primary: "#0284C7",
  primaryDark: "#0369A1",
  success: "#16A34A",
  successLight: "#DCFCE7",
  error: "#DC2626",
  errorLight: "#FEE2E2",
  warning: "#D97706",
  neutral: "#64748B",
  background: "#F8FAFC",
  cardBorder: "#E2E8F0",
};

const CHART_COLORS = [
  "#0284C7", // Sky blue
  "#10B981", // Emerald green
  "#6366F1", // Indigo
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#8B5CF6", // Purple
  "#06B6D4", // Cyan
  "#F97316", // Orange
];

const chartTooltipStyle = {
  backgroundColor: "#FFFFFF",
  borderColor: "#E2E8F0",
  borderRadius: 8,
  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  color: "#0F172A",
  fontSize: "0.85rem",
  padding: "8px 12px",
};

const Dashboard = () => {
  const { fetchBestStocks } = usePortfolio();
  const { holdings, stats, fetchPortfolio, isInitialized } = usePortfolioStore();
  const [bestStocks, setBestStocks] = useState<any[]>([]);
  const [adminData, setAdminData] = useState<any>(null);

  // Tabs: 0 = Stock Trading Analytics, 1 = Options Trading Analytics
  const [activeTab, setActiveTab] = useState<number>(0);

  // Date Range State & Validation
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [dateError, setDateError] = useState<string | null>(null);

  // Stock Filter State
  const [tradeMode, setTradeMode] = useState<"LIVE" | "PAPER" | "BOTH">("BOTH");
  const [selectedUserId, setSelectedUserId] = useState<string>("ALL");
  const [market, setMarket] = useState<"INDIA" | "US">("INDIA");
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  // Options Trading Analytics State
  const [optionsData, setOptionsData] = useState<any>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsUnderlying, setOptionsUnderlying] = useState<string>("ALL");

  const authContext = useContext(AuthContext);
  const isAdmin = authContext?.isAdmin || false;

  // Validation function for date selection
  const validateDateRange = (start: string, end: string): boolean => {
    const today = moment().format("YYYY-MM-DD");
    if (start && end && moment(start).isAfter(moment(end))) {
      setDateError("Start date cannot be after end date");
      return false;
    }
    if (start && moment(start).isAfter(today)) {
      setDateError("Start date cannot be in the future");
      return false;
    }
    if (end && moment(end).isAfter(today)) {
      setDateError("End date cannot be in the future");
      return false;
    }
    setDateError(null);
    return true;
  };

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    const valid = validateDateRange(val, endDate);
    if (valid) {
      setDateRange({ start: val, end: endDate });
    }
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    const valid = validateDateRange(startDate, val);
    if (valid) {
      setDateRange({ start: startDate, end: val });
    }
  };

  const handleDatePreset = (preset: string) => {
    const end = moment().format("YYYY-MM-DD");
    let start = "";
    if (preset === "Today") start = moment().format("YYYY-MM-DD");
    else if (preset === "7D") start = moment().subtract(7, "days").format("YYYY-MM-DD");
    else if (preset === "30D") start = moment().subtract(30, "days").format("YYYY-MM-DD");
    else if (preset === "All") start = "";

    setStartDate(start);
    setEndDate(start ? end : "");
    setDateError(null);
    setDateRange({ start, end: start ? end : "" });
  };

  const fetchAdminDashboard = async (
    range?: { start: string; end: string },
    mode?: string,
    userId?: string,
    selectedMarket?: string
  ) => {
    if (!isAdmin) return;
    setLoadingAdmin(true);
    try {
      const params: any = range?.start ? { startDate: range.start, endDate: range.end } : {};
      if (mode === "LIVE") params.isPaper = false;
      else if (mode === "PAPER") params.isPaper = true;
      if (userId && userId !== "ALL") params.targetUserId = userId;
      if (selectedMarket) params.market = selectedMarket;

      const res = await api.get("/admin/dashboard", { params });
      setAdminData(res.data);
    } catch (err) {
      console.error("Admin data fetch failed:", err);
    } finally {
      setLoadingAdmin(false);
    }
  };

  const fetchOptionsAnalytics = async (
    range?: { start: string; end: string },
    und?: string
  ) => {
    if (!isAdmin) return;
    setLoadingOptions(true);
    try {
      const params: any = {};
      if (range?.start) params.startDate = range.start;
      if (range?.end) params.endDate = range.end;
      if (und && und !== "ALL") params.underlying = und;

      const res = await api.get("/options/analytics", { params });
      setOptionsData(res.data);
    } catch (err) {
      console.error("Options analytics fetch failed:", err);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      if (!isInitialized) fetchPortfolio();
      if (activeTab === 0) {
        fetchAdminDashboard(dateRange, tradeMode, selectedUserId, market);
      } else if (activeTab === 1) {
        fetchOptionsAnalytics(dateRange, optionsUnderlying);
      }
    }
    fetchBestStocks().then((data) => setBestStocks(data || []));
  }, [
    isAdmin,
    isInitialized,
    dateRange,
    tradeMode,
    selectedUserId,
    market,
    activeTab,
    optionsUnderlying,
  ]);

  // Export Stock Trades
  const downloadJSON = async () => {
    if (dateError) {
      alert(`Invalid date range: ${dateError}`);
      return;
    }
    try {
      const params: any = dateRange.start ? { startDate: dateRange.start, endDate: dateRange.end } : {};
      if (tradeMode === "LIVE") params.isPaper = false;
      else if (tradeMode === "PAPER") params.isPaper = true;
      if (selectedUserId && selectedUserId !== "ALL") params.targetUserId = selectedUserId;
      params.market = market;

      const res = await api.get("/admin/export-trades", { params });
      const trades = res.data;
      if (!trades || !trades.length) return alert("No trades found for this period");

      const jsonString = JSON.stringify(trades, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const startTag = dateRange.start || "all";
      const endTag = dateRange.end ? `_to_${dateRange.end}` : "";
      a.download = `stock_trades_${market.toLowerCase()}_${startTag}${endTag}.json`;
      a.click();
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export trades");
    }
  };

  // Export Options Trades
  const downloadOptionsJSON = async () => {
    if (dateError) {
      alert(`Invalid date range: ${dateError}`);
      return;
    }
    try {
      const params: any = {};
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      if (optionsUnderlying && optionsUnderlying !== "ALL") params.underlying = optionsUnderlying;

      const res = await api.get("/options/export-trades", { params });
      const positions = res.data;
      if (!positions || !positions.length) return alert("No options positions found for this period");

      const jsonString = JSON.stringify(positions, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const startTag = dateRange.start || "all";
      const endTag = dateRange.end ? `_to_${dateRange.end}` : "";
      a.download = `options_trades_${optionsUnderlying.toLowerCase()}_${startTag}${endTag}.json`;
      a.click();
    } catch (err) {
      console.error("Options export failed", err);
      alert("Failed to export options trades");
    }
  };

  const formatDailyPnl = (val: number, isUs = false) => {
    if (val === 0) return "0";
    const sign = val > 0 ? "+" : "-";
    const curr = isUs ? "$" : "₹";
    const abs = Math.abs(val);

    if (abs >= 1000) {
      return `${sign}${curr}${(abs / 1000).toFixed(1)}k`;
    }
    return `${sign}${curr}${Math.round(abs)}`;
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: theme.background }}>
      {/* Top Tabs: Stock Trading Analytics vs Options Trading Analytics */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "1.05rem",
              minHeight: 48,
            },
          }}
        >
          <Tab label="Stock Trading Analytics" />
          <Tab label="Options Trading Analytics" />
        </Tabs>
      </Box>

      {/* Shared Date Range Picker with Strict Validation */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
          Date Range:
        </Typography>

        <TextField
          size="small"
          type="date"
          label="From"
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: moment().format("YYYY-MM-DD") }}
          error={Boolean(dateError)}
          sx={{ width: 165 }}
        />

        <TextField
          size="small"
          type="date"
          label="To"
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: moment().format("YYYY-MM-DD") }}
          error={Boolean(dateError)}
          sx={{ width: 165 }}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          {["Today", "7D", "30D", "All"].map((p) => {
            const isSelected =
              dateRange.start ===
              (p === "Today"
                ? moment().format("YYYY-MM-DD")
                : p === "All"
                ? ""
                : moment().subtract(p === "7D" ? 7 : 30, "days").format("YYYY-MM-DD"));
            return (
              <Button
                key={p}
                variant={isSelected ? "contained" : "outlined"}
                size="small"
                onClick={() => handleDatePreset(p)}
                sx={{
                  borderRadius: 4,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: isSelected ? 1 : 0,
                }}
              >
                {p}
              </Button>
            );
          })}
        </Box>

        {dateError && (
          <Alert severity="error" sx={{ py: 0, px: 2, alignItems: "center", fontSize: "0.85rem" }}>
            {dateError}
          </Alert>
        )}
      </Paper>

      {/* ================= TAB 0: STOCK TRADING ANALYTICS ================= */}
      {activeTab === 0 && isAdmin && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" color={theme.primary} sx={{ fontWeight: "bold" }}>
                Stock Admin Analytics
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Viewing:{" "}
                {tradeMode === "BOTH"
                  ? "All Trades"
                  : tradeMode === "LIVE"
                  ? "Live Only"
                  : "Paper Only"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              {/* Market filter toggle (Temporarily disabled for US intraday)
              <Box sx={{ display: "flex", bgcolor: "#eee", p: 0.5, borderRadius: 2 }}>
                {["INDIA", "US"].map((m) => (
                  <Button
                    key={m}
                    size="small"
                    variant={market === m ? "contained" : "text"}
                    onClick={() => setMarket(m as any)}
                    sx={{
                      px: 2,
                      borderRadius: 1.5,
                      textTransform: "none",
                      boxShadow: market === m ? 1 : 0,
                    }}
                  >
                    {m}
                  </Button>
                ))}
              </Box>
              */}

              <Box sx={{ display: "flex", bgcolor: "#eee", p: 0.5, borderRadius: 2 }}>
                {["LIVE", "PAPER", "BOTH"].map((m) => (
                  <Button
                    key={m}
                    size="small"
                    variant={tradeMode === m ? "contained" : "text"}
                    onClick={() => setTradeMode(m as any)}
                    sx={{
                      px: 2,
                      borderRadius: 1.5,
                      textTransform: "none",
                      boxShadow: tradeMode === m ? 1 : 0,
                    }}
                  >
                    {m}
                  </Button>
                ))}
              </Box>

              <Box sx={{ width: 170 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    sx={{ borderRadius: 2, bgcolor: "white" }}
                  >
                    <MenuItem value="ALL">All Users</MenuItem>
                    {(adminData?.userList || []).map((u: any) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={downloadJSON}
                disabled={Boolean(dateError)}
                sx={{ borderRadius: 4, textTransform: "none" }}
              >
                Download Stock Logs (JSON)
              </Button>
            </Box>
          </Box>

          {loadingAdmin ? (
            <Typography align="center" sx={{ my: 10 }}>
              Loading Stock Analytics...
            </Typography>
          ) : adminData ? (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {selectedUserId === "ALL" && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: 3,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography variant="subtitle2" color="textSecondary">
                        Total Users
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                        {adminData.topMetrics?.totalUsers}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6, md: selectedUserId === "ALL" ? 3 : 4 }}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      Win Rate
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", mt: 1, color: theme.success }}
                    >
                      {adminData.topMetrics?.winRate?.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: selectedUserId === "ALL" ? 3 : 4 }}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      Total PnL
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        mt: 1,
                        color:
                          (adminData.topMetrics?.totalPlatformPnl || 0) >= 0
                            ? theme.success
                            : theme.error,
                      }}
                    >
                      {market === "US" ? "$" : "₹"}
                      {formatPrice(adminData.topMetrics?.totalPlatformPnl)}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedUserId !== "ALL" && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: 3,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography variant="subtitle2" color="textSecondary">
                        Mode
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", mt: 1, color: theme.primary }}
                      >
                        {tradeMode}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* PnL Calendar Widget */}
              <Typography variant="h6" color={theme.primary} sx={{ mb: 2, fontWeight: "bold" }}>
                Historical PnL (Daily)
              </Typography>
              <Card sx={{ p: 2, mb: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", borderRadius: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  {(adminData.insights?.dailyPnL || []).slice(-30).map((day: any) => (
                    <Box
                      key={day.date}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: day.pnl > 0 ? "#e8f5e9" : day.pnl < 0 ? "#ffebee" : "#f5f5f5",
                        border: `1px solid ${
                          day.pnl > 0 ? theme.success : day.pnl < 0 ? theme.error : "#ddd"
                        }`,
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        {moment(day.date).format("MMM DD")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: day.pnl >= 0 ? theme.success : theme.error,
                        }}
                      >
                        {formatDailyPnl(day.pnl, market === "US")}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>

              {selectedUserId === "ALL" && adminData.userSummaries && (
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    mb: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                    User Performance
                  </Typography>
                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #eee", textAlign: "left" }}>
                          <th style={{ padding: "12px" }}>User</th>
                          <th style={{ padding: "12px" }}>Today's PnL</th>
                          <th style={{ padding: "12px" }}>Overall PnL</th>
                          <th style={{ padding: "12px" }}>Active/Closed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminData.userSummaries.map((u: any) => (
                          <tr key={u.id} style={{ borderBottom: "1px solid #f9f9f9" }}>
                            <td style={{ padding: "12px" }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                {u.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {u.email}
                              </Typography>
                            </td>
                            <td style={{ padding: "12px" }}>
                              <Typography
                                color={u.todayPnl >= 0 ? "success.main" : "error.main"}
                                sx={{ fontWeight: "bold" }}
                              >
                                {market === "US" ? "$" : "₹"}
                                {formatPrice(u.todayPnl)}
                              </Typography>
                            </td>
                            <td style={{ padding: "12px" }}>
                              <Typography
                                color={u.overallPnl >= 0 ? "success.main" : "error.main"}
                                sx={{ fontWeight: "bold" }}
                              >
                                {market === "US" ? "$" : "₹"}
                                {formatPrice(u.overallPnl)}
                              </Typography>
                            </td>
                            <td style={{ padding: "12px" }}>
                              <Typography variant="body2">
                                {u.activeTradesCount} / {u.closedTradesCount}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              )}

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1E293B" }}>
                Insights & Performance Analytics
              </Typography>

              {/* Cumulative Equity Curve */}
              {adminData.insights?.dailyPnL && adminData.insights.dailyPnL.length > 0 && (
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2.5,
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155" }}>
                      CUMULATIVE PERFORMANCE & EQUITY CURVE
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#0284C7", fontWeight: 700 }}>
                      ● Net Growth Trajectory
                    </Typography>
                  </Box>
                  <Box sx={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={adminData.insights.dailyPnL.map((day: any, idx: number, arr: any[]) => {
                          const cumulativePnl = arr.slice(0, idx + 1).reduce((acc, curr) => acc + (curr.pnl || 0), 0);
                          return {
                            date: moment(day.date).format("MMM DD"),
                            dailyPnl: day.pnl,
                            cumulativePnl,
                          };
                        })}
                        margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="stockEquityGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0284C7" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#0284C7" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                        <YAxis
                          stroke="#64748B"
                          fontSize={11}
                          tickFormatter={(v) => `${market === "US" ? "$" : "₹"}${formatPrice(v)}`}
                        />
                        <ChartTooltip
                          contentStyle={chartTooltipStyle}
                          formatter={(value: any, name?: any): any => [
                            `${market === "US" ? "$" : "₹"}${formatPrice(Number(value))}`,
                            name === "cumulativePnl" ? "Cumulative P&L" : "Daily P&L",
                          ]}
                        />
                        <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                        <Area
                          type="monotone"
                          dataKey="cumulativePnl"
                          stroke="#0284C7"
                          strokeWidth={2.5}
                          fill="url(#stockEquityGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              )}

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                      bgcolor: "#FFFFFF",
                      height: 380,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 2 }}>
                      TIME-WINDOW PROFITABILITY ATTRIBUTION
                    </Typography>
                    <Box sx={{ flexGrow: 1, width: "100%", height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={adminData.insights?.timeBased || []}
                          margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                          <XAxis dataKey="timeWindow" stroke="#64748B" fontSize={11} />
                          <YAxis
                            stroke="#64748B"
                            fontSize={11}
                            tickFormatter={(v) => `${market === "US" ? "$" : "₹"}${formatPrice(v)}`}
                          />
                          <ChartTooltip
                            contentStyle={chartTooltipStyle}
                            formatter={(val: any) => [
                              `${market === "US" ? "$" : "₹"}${formatPrice(val)}`,
                              "Attributed P&L",
                            ]}
                          />
                          <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                          <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                            {(adminData.insights?.timeBased || []).map((entry: any, index: number) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.pnl >= 0 ? theme.success : theme.error}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                      bgcolor: "#FFFFFF",
                      height: 380,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 2 }}>
                      SECTOR ALLOCATION & PROFIT WEIGHTING
                    </Typography>
                    <Box sx={{ flexGrow: 1, width: "100%", height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={adminData.insights?.sectorPerformance || []}
                            dataKey="pnl"
                            nameKey="sector"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={85}
                            paddingAngle={3}
                          >
                            {(adminData.insights?.sectorPerformance || []).map((_: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <ChartTooltip
                            contentStyle={chartTooltipStyle}
                            formatter={(val: any, name?: any): any => [
                              `${market === "US" ? "$" : "₹"}${formatPrice(val)}`,
                              name || "Sector",
                            ]}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: "0.8rem", color: "#64748B" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography align="center" sx={{ my: 10 }}>
              No analytics data available for this period.
            </Typography>
          )}
        </>
      )}

      {/* ================= TAB 1: OPTIONS TRADING ANALYTICS ================= */}
      {activeTab === 1 && isAdmin && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" color={theme.primary} sx={{ fontWeight: "bold" }}>
                Options Trading Analytics
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Systematic Structure & Hedged Options Performance
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Box sx={{ width: 160 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={optionsUnderlying}
                    onChange={(e) => setOptionsUnderlying(e.target.value)}
                    sx={{ borderRadius: 2, bgcolor: "white" }}
                  >
                    <MenuItem value="ALL">All Underlyings</MenuItem>
                    <MenuItem value="NIFTY">NIFTY</MenuItem>
                    <MenuItem value="BANKNIFTY">BANKNIFTY</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={downloadOptionsJSON}
                disabled={Boolean(dateError)}
                sx={{ borderRadius: 4, textTransform: "none" }}
              >
                Download Options Logs (JSON)
              </Button>
            </Box>
          </Box>

          {loadingOptions ? (
            <Typography align="center" sx={{ my: 10 }}>
              Loading Options Analytics...
            </Typography>
          ) : optionsData ? (
            <>
              {/* Top Options Metrics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Positions
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                      {optionsData.topMetrics?.totalPositions || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Active: {optionsData.topMetrics?.activePositions || 0} | Closed:{" "}
                      {optionsData.topMetrics?.closedPositions || 0}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      Win Rate
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", mt: 1, color: theme.success }}
                    >
                      {optionsData.topMetrics?.winRate?.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {optionsData.topMetrics?.winningTrades || 0}W -{" "}
                      {optionsData.topMetrics?.losingTrades || 0}L
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Realized P&L
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        mt: 1,
                        color:
                          (optionsData.topMetrics?.totalPnl || 0) >= 0
                            ? theme.success
                            : theme.error,
                      }}
                    >
                      ₹{formatPrice(optionsData.topMetrics?.totalPnl || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Net across closed positions
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      Active Positions
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", mt: 1, color: theme.primary }}
                    >
                      {optionsData.topMetrics?.activePositions || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Currently monitored live/paper
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Daily Options PnL Calendar */}
              {optionsData.dailyPnL && optionsData.dailyPnL.length > 0 && (
                <>
                  <Typography
                    variant="h6"
                    color={theme.primary}
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Options Daily P&L History
                  </Typography>
                  <Card
                    sx={{
                      p: 2,
                      mb: 4,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                      borderRadius: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      {optionsData.dailyPnL.slice(-30).map((day: any) => (
                        <Box
                          key={day.date}
                          sx={{
                            width: 85,
                            height: 80,
                            borderRadius: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor:
                              day.pnl > 0
                                ? "#e8f5e9"
                                : day.pnl < 0
                                ? "#ffebee"
                                : "#f5f5f5",
                            border: `1px solid ${
                              day.pnl > 0
                                ? theme.success
                                : day.pnl < 0
                                ? theme.error
                                : "#ddd"
                            }`,
                          }}
                        >
                          <Typography variant="caption" color="textSecondary">
                            {moment(day.date).format("MMM DD")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: day.pnl >= 0 ? theme.success : theme.error,
                            }}
                          >
                            {formatDailyPnl(day.pnl)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </>
              )}

              {/* Options Cumulative Realized P&L Curve */}
              {optionsData.dailyPnL && optionsData.dailyPnL.length > 0 && (
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2.5,
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155" }}>
                      OPTIONS CUMULATIVE REALIZED P&L TRAJECTORY
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#16A34A", fontWeight: 700 }}>
                      ● Systematic Growth Curve
                    </Typography>
                  </Box>
                  <Box sx={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={optionsData.dailyPnL.map((day: any, idx: number, arr: any[]) => {
                          const cumulativePnl = arr.slice(0, idx + 1).reduce((acc, curr) => acc + (curr.pnl || 0), 0);
                          return {
                            date: moment(day.date).format("MMM DD"),
                            dailyPnl: day.pnl,
                            cumulativePnl,
                          };
                        })}
                        margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="optionsEquityGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#16A34A" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#16A34A" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                        <YAxis
                          stroke="#64748B"
                          fontSize={11}
                          tickFormatter={(v) => `₹${formatPrice(v)}`}
                        />
                        <ChartTooltip
                          contentStyle={chartTooltipStyle}
                          formatter={(value: any, name?: any): any => [
                            `₹${formatPrice(Number(value))}`,
                            name === "cumulativePnl" ? "Cumulative Realized P&L" : "Daily P&L",
                          ]}
                        />
                        <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                        <Area
                          type="monotone"
                          dataKey="cumulativePnl"
                          stroke="#16A34A"
                          strokeWidth={2.5}
                          fill="url(#optionsEquityGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              )}

              {/* Strategy Performance Breakdown */}
              {optionsData.strategyPerformance &&
                optionsData.strategyPerformance.length > 0 && (
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      border: "1px solid #E2E8F0",
                      mb: 4,
                      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 2 }}>
                      STRATEGY PROFIT ATTRIBUTION & WIN RATE
                    </Typography>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ width: "100%", height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={optionsData.strategyPerformance}
                              margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                              <XAxis dataKey="strategy" stroke="#64748B" fontSize={11} />
                              <YAxis
                                stroke="#64748B"
                                fontSize={11}
                                tickFormatter={(v) => `₹${formatPrice(v)}`}
                              />
                              <ChartTooltip
                                contentStyle={chartTooltipStyle}
                                formatter={(val: any) => [`₹${formatPrice(val)}`, "Realized P&L"]}
                              />
                              <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                              <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                                {optionsData.strategyPerformance.map((sp: any, idx: number) => (
                                  <Cell
                                    key={`cell-opt-${idx}`}
                                    fill={sp.pnl >= 0 ? theme.success : theme.error}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ width: "100%", height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={optionsData.strategyPerformance}
                              margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                              <XAxis dataKey="strategy" stroke="#64748B" fontSize={11} />
                              <YAxis
                                stroke="#64748B"
                                fontSize={11}
                                tickFormatter={(v) => `${v}%`}
                                domain={[0, 100]}
                              />
                              <ChartTooltip
                                contentStyle={chartTooltipStyle}
                                formatter={(val: any) => [`${val}%`, "Win Rate"]}
                              />
                              <ReferenceLine y={50} stroke="#94A3B8" strokeDasharray="3 3" label="50% Target" />
                              <Bar dataKey="winRate" fill="#0284C7" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                          <TableRow sx={{ borderBottom: "2px solid #E2E8F0" }}>
                            <TableCell sx={{ fontWeight: "bold" }}>Strategy</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              Trades Count
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              Win Rate
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              Total Realized P&L
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {optionsData.strategyPerformance.map((sp: any) => (
                            <TableRow key={sp.strategy} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{sp.strategy}</TableCell>
                              <TableCell align="center">{sp.count}</TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  color: sp.winRate >= 50 ? theme.success : theme.error,
                                  fontWeight: "bold",
                                }}
                              >
                                {sp.winRate}%
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: "bold",
                                  color: sp.pnl >= 0 ? theme.success : theme.error,
                                }}
                              >
                                ₹{formatPrice(sp.pnl)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                )}

              {/* Recent Options Positions History Table */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  mb: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Recent Options Positions
                </Typography>
                {optionsData.positions && optionsData.positions.length > 0 ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ borderBottom: "2px solid #eee" }}>
                          <TableCell sx={{ fontWeight: "bold" }}>Strategy</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Underlying</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Legs Summary</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Entry Time</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Exit Time / Reason</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            Realized P&L
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {optionsData.positions.map((pos: any) => {
                          const pnl =
                            pos.realizedPnl !== undefined ? pos.realizedPnl : pos.pnl || 0;
                          return (
                            <TableRow key={pos.strategyId || pos._id} hover>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {pos.strategyName}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={pos.underlying}
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={pos.status}
                                  size="small"
                                  color={
                                    pos.status === "CLOSED"
                                      ? pnl >= 0
                                        ? "success"
                                        : "error"
                                      : pos.status === "OPEN"
                                      ? "primary"
                                      : "default"
                                  }
                                  variant="outlined"
                                  sx={{ fontWeight: "bold" }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                                {(pos.legs || [])
                                  .map(
                                    (l: any) =>
                                      `${l.side} ${l.strike} ${l.optionType} (${l.quantity})`
                                  )
                                  .join(", ") || "-"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                {pos.entryTime
                                  ? moment(pos.entryTime).format("YYYY-MM-DD HH:mm")
                                  : "-"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                {pos.exitTime ? (
                                  <>
                                    {moment(pos.exitTime).format("YYYY-MM-DD HH:mm")}
                                    {pos.exitReason && (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="textSecondary"
                                      >
                                        {pos.exitReason}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  "Holding"
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: "bold",
                                  color: pnl >= 0 ? theme.success : theme.error,
                                }}
                              >
                                ₹{formatPrice(pnl)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Typography color="textSecondary" sx={{ py: 3, textAlign: "center" }}>
                    No options positions recorded for this time window.
                  </Typography>
                )}
              </Paper>
            </>
          ) : (
            <Typography align="center" sx={{ my: 10 }}>
              No options analytics data available for this period.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
